import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlaneIcon, MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Checkbox } from '../components/ui/Checkbox';
// Floating cloud component
function FloatingCloud({
  delay = 0,
  top = '20%'
}: {
  delay?: number;
  top?: string;
}) {
  return <motion.div className="absolute pointer-events-none opacity-[0.05]" style={{
    top
  }} initial={{
    x: '-20%'
  }} animate={{
    x: '120vw'
  }} transition={{
    duration: 60 + delay * 10,
    repeat: Infinity,
    ease: 'linear',
    delay
  }} data-id="element-1198">
      <svg width={120} height={60} viewBox="0 0 200 100" fill="none" data-id="element-1199">
        <ellipse cx="50" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1200" />
        <ellipse cx="80" cy="50" rx="45" ry="30" fill="#0066FF" data-id="element-1201" />
        <ellipse cx="120" cy="55" rx="40" ry="28" fill="#0066FF" data-id="element-1202" />
        <ellipse cx="150" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1203" />
      </svg>
    </motion.div>;
}
export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    // Mock authentication logic
    if (email && password) {
      const emailLower = email.toLowerCase();
      // Check for admin/regulator credentials
      if (emailLower.includes('admin')) {
        navigate(ROUTES.ADMIN);
      } else if (emailLower.includes('regulator') || emailLower.includes('ncaa')) {
        navigate(ROUTES.REGULATOR);
      } else {
        // Redirect customers to their wallet/dashboard
        navigate(ROUTES.WALLET);
      }
    } else {
      setError('Please enter your email and password');
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 relative overflow-hidden" data-id="element-1204">
      {/* Floating Clouds */}
      <div className="fixed inset-0 pointer-events-none" data-id="element-1205">
        <FloatingCloud delay={0} top="15%" data-id="element-1206" />
        <FloatingCloud delay={15} top="45%" data-id="element-1207" />
        <FloatingCloud delay={30} top="75%" data-id="element-1208" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-primary-200 rounded-full blur-3xl opacity-50 animate-float" data-id="element-1209" />
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-accent-200 rounded-full blur-3xl opacity-50 animate-float" style={{
      animationDelay: '1s'
    }} data-id="element-1210" />

      <div className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12" data-id="element-1211">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-md" data-id="element-1212">
          {/* Logo */}
          <Link to={ROUTES.HOME} className="flex items-center justify-center gap-3 mb-8" data-id="element-1213">
            <motion.div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" whileHover={{
            scale: 1.05,
            rotate: -5
          }} data-id="element-1214">
              <PlaneIcon className="w-7 h-7 text-white" data-id="element-1215" />
            </motion.div>
            <span className="text-display-sm font-bold text-surface-900" data-id="element-1216">
              AeroZeep
            </span>
          </Link>

          {/* Login Card */}
          <Card variant="elevated" padding="lg" className="shadow-2xl border-0" data-id="element-1217">
            <div className="text-center mb-8" data-id="element-1218">
              <h1 className="text-display-sm font-bold text-surface-900 mb-2" data-id="element-1219">
                Welcome Back
              </h1>
              <p className="text-body-md text-surface-600" data-id="element-1220">
                Sign in to manage your refunds and bookings
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4" data-id="element-1221">
              <Input label="Email Address" type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} leftIcon={<MailIcon className="w-4 h-4" data-id="element-1223" />} required data-id="element-1222" />

              <div className="relative" data-id="element-1224">
                <Input label="Password" type={showPassword ? 'text' : 'password'} placeholder="Enter your password" value={password} onChange={e => setPassword(e.target.value)} leftIcon={<LockIcon className="w-4 h-4" data-id="element-1226" />} required data-id="element-1225" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[38px] text-surface-400 hover:text-surface-600 transition-colors" data-id="element-1227">
                  {showPassword ? <EyeOffIcon className="w-4 h-4" data-id="element-1228" /> : <EyeIcon className="w-4 h-4" data-id="element-1229" />}
                </button>
              </div>

              {error && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="p-3 bg-error-50 border border-error-200 rounded-lg" data-id="element-1230">
                  <p className="text-body-sm text-error-700" data-id="element-1231">{error}</p>
                </motion.div>}

              <div className="flex items-center justify-between" data-id="element-1232">
                <Checkbox id="remember" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} label="Remember me" data-id="element-1233" />
                <Link to="/forgot-password" className="text-body-sm font-medium text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1234">
                  Forgot password?
                </Link>
              </div>

              <Button type="submit" fullWidth loading={isLoading} icon={<ArrowRightIcon className="w-4 h-4" data-id="element-1236" />} iconPosition="right" className="shadow-lg shadow-primary-500/20" data-id="element-1235">
                Sign In
              </Button>
            </form>

            {/* Demo Accounts */}
            <div className="mt-6 p-4 bg-surface-50 rounded-xl border border-surface-200" data-id="element-1237">
              <p className="text-body-sm font-medium text-surface-700 mb-2" data-id="element-1238">
                Demo Accounts:
              </p>
              <div className="space-y-1 text-caption text-surface-600" data-id="element-1239">
                <p data-id="element-1240">• Customer: any email + any password</p>
                <p data-id="element-1241">• Admin: admin@aerozeep.com + any password</p>
                <p data-id="element-1242">• Regulator: ncaa@regulator.gov.ng + any password</p>
              </div>
            </div>

            {/* Sign Up Link */}
            <div className="mt-6 text-center" data-id="element-1243">
              <p className="text-body-sm text-surface-600" data-id="element-1244">
                Don't have an account?{' '}
                <Link to={ROUTES.SIGNUP} className="font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-1245">
                  Sign up
                </Link>
              </p>
            </div>
          </Card>

          {/* Trust Indicators */}
          <div className="flex items-center justify-center gap-6 mt-8 text-surface-500" data-id="element-1246">
            <div className="flex items-center gap-2" data-id="element-1247">
              <ShieldCheckIcon className="w-4 h-4 text-success-500" data-id="element-1248" />
              <span className="text-body-sm" data-id="element-1249">NCAA Compliant</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-1250">
              <SparklesIcon className="w-4 h-4 text-accent-500" data-id="element-1251" />
              <span className="text-body-sm" data-id="element-1252">Secure Login</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}